import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';

const DISPLAY_ROLE = (role) => role === 'super_admin' ? 'ICSC Governing Body' : 'Certification Body';

const STATUS_COLOR = {
  approved:               { bg: '#d1fae5', color: '#065f46' },
  rejected:               { bg: '#fee2e2', color: '#991b1b' },
  pending:                { bg: '#fff3cd', color: '#856404' },
  requires_clarification: { bg: '#e0f2fe', color: '#0369a1' },
};

const RECORD_TYPE_LABEL = {
  stage_1_audit: 'Stage 1 Audit',
  stage_2_audit: 'Stage 2 Audit',
  certification:  'Certification',
};

function Evidence() {
  const user    = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['super_admin', 'third_party_admin'].includes(user.role);

  // Certifications (audit records) for this company
  const [certifications,   setCertifications]   = useState([]);
  const [selectedIso,      setSelectedIso]       = useState('');   // ISO standard string e.g. "ISO 9001"
  const [selectedCertId,   setSelectedCertId]    = useState('');   // specific certification_id
  const [submissions,      setSubmissions]       = useState([]);
  const [activeTab,        setActiveTab]         = useState('upload');
  const [loading,          setLoading]           = useState(false);
  const [message,          setMessage]           = useState('');
  const [error,            setError]             = useState('');

  const [form, setForm] = useState({
    clause_number: '',
    clause_title: '',
    evidence_title: '',
    evidence_description: '',
    file: null,
  });

  // Unique ISO standards from certifications
  const isoOptions = [...new Set(certifications.map(c => c.iso_standard).filter(Boolean))].sort();

  // Certifications filtered to selected ISO standard
  const auditOptions = certifications.filter(c => c.iso_standard === selectedIso);

  useEffect(() => {
    axiosClient.get('/certifications/certifications/')
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setCertifications(data);
        if (data.length > 0) {
          const firstIso = data[0].iso_standard;
          setSelectedIso(firstIso);
          setSelectedCertId(String(data[0].id));
        }
      })
      .catch(() => setError('Could not load your certification records.'));
  }, []);

  useEffect(() => {
    // Auto-select first cert when ISO standard changes
    const match = certifications.find(c => c.iso_standard === selectedIso);
    if (match) setSelectedCertId(String(match.id));
  }, [selectedIso, certifications]);

  useEffect(() => {
    if (activeTab === 'submissions') loadSubmissions();
  }, [activeTab, selectedCertId]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const params = selectedCertId ? { certification_id: selectedCertId } : {};
      const r = await axiosClient.get('/evidence/my-submissions', { params });
      const items = Array.isArray(r.data) ? r.data : (r.data?.items ?? []);
      setSubmissions(items);
    } catch { setSubmissions([]); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    if (!form.file)         { setError('Please select a file to upload.'); return; }
    if (!selectedCertId)    { setError('Please select an audit record.'); return; }
    if (!form.clause_number){ setError('Please enter a clause number.'); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('certification_id',     selectedCertId);
      fd.append('clause_number',        form.clause_number);
      fd.append('clause_title',         form.clause_title);
      fd.append('evidence_title',       form.evidence_title);
      fd.append('evidence_description', form.evidence_description || '');
      fd.append('file',                 form.file);
      await axiosClient.post('/evidence/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('Evidence submitted. Your certification body will review it.');
      setForm({ clause_number: '', clause_title: '', evidence_title: '', evidence_description: '', file: null });
      document.getElementById('ev-file').value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit evidence.');
    } finally { setLoading(false); }
  };

  const handleDownload = async (id, fileName) => {
    try {
      const r = await axiosClient.get(`/evidence/download/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.setAttribute('download', fileName || `evidence_${id}`);
      document.body.appendChild(a); a.click(); a.remove();
    } catch { setError('Failed to download file.'); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' };
  const fieldWrap  = { marginBottom: '16px' };

  const TAB_BTN = (key, label) => (
    <button key={key} onClick={() => setActiveTab(key)} style={{
      padding: '10px 22px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: activeTab === key ? '700' : '400',
      background: 'transparent', color: activeTab === key ? '#1a3a5c' : '#6b7280',
      borderBottom: activeTab === key ? '3px solid #1a3a5c' : '3px solid transparent',
    }}>{label}</button>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: '22px', color: '#1a3a5c' }}>Evidence Portal</h2>
      <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '14px' }}>
        Upload documents against your active audit records. Your certification body and ICSC Governing Body can view and review them.
      </p>

      {/* ── Standard + Audit selectors ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div>
          <label style={labelStyle}>ISO Standard</label>
          <select value={selectedIso} onChange={e => setSelectedIso(e.target.value)} style={inputStyle}>
            {isoOptions.length === 0 && <option value="">No certifications found</option>}
            {isoOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Audit Record</label>
          <select value={selectedCertId} onChange={e => setSelectedCertId(e.target.value)} style={inputStyle}>
            {auditOptions.map(c => (
              <option key={c.id} value={c.id}>
                {RECORD_TYPE_LABEL[c.record_type] || c.record_type} — {c.audit_date ? new Date(c.audit_date).toLocaleDateString() : new Date(c.issue_date).toLocaleDateString()}
                {c.certificate_number ? ` (${c.certificate_number})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '24px', display: 'flex', gap: '4px' }}>
        {TAB_BTN('upload',      'Upload Evidence')}
        {TAB_BTN('submissions', 'My Submissions')}
      </div>

      {/* ── Messages ── */}
      {message && <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>{message}</div>}
      {error   && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

      {/* ── Upload tab ── */}
      {activeTab === 'upload' && (
        <form onSubmit={handleSubmit} style={{ background: '#f9fafb', padding: '24px', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={fieldWrap}>
              <label style={labelStyle}>Clause Number *</label>
              <input value={form.clause_number} onChange={e => setForm({...form, clause_number: e.target.value})}
                placeholder="e.g. 4.1, 7.5.2" required style={inputStyle} />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Clause Title *</label>
              <input value={form.clause_title} onChange={e => setForm({...form, clause_title: e.target.value})}
                placeholder="e.g. Context of the organization" required style={inputStyle} />
            </div>
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Evidence Title *</label>
            <input value={form.evidence_title} onChange={e => setForm({...form, evidence_title: e.target.value})}
              placeholder="e.g. Quality Policy Document" required style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.evidence_description} onChange={e => setForm({...form, evidence_description: e.target.value})}
              rows={3} placeholder="What does this document demonstrate?" style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>File *</label>
            <input id="ev-file" type="file" onChange={e => setForm({...form, file: e.target.files[0]})} required style={inputStyle} />
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>PDF, DOCX, XLSX, PNG, JPG — max 10 MB</p>
          </div>
          <button type="submit" disabled={loading} style={{
            padding: '12px 28px', background: loading ? '#9ca3af' : '#1a3a5c', color: '#fff',
            border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? 'Submitting…' : 'Submit Evidence'}
          </button>
        </form>
      )}

      {/* ── Submissions tab ── */}
      {activeTab === 'submissions' && (
        <div>
          {loading ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Loading…</p>
          ) : submissions.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>No evidence submitted for this audit record yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {submissions.map(s => {
                const sc  = STATUS_COLOR[s.status] || STATUS_COLOR.pending;
                const rev = s.reviewed_by_role ? DISPLAY_ROLE(s.reviewed_by_role) : s.reviewed_by_name || null;
                return (
                  <div key={s.id} style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: '700', fontSize: '15px', color: '#111827' }}>{s.evidence_title}</p>
                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                          Clause {s.clause_number}: {s.clause_title}
                        </p>
                        {s.iso_standard && (
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                            {s.iso_standard}{s.certification_number ? ` — ${s.certification_number}` : ''}
                          </p>
                        )}
                      </div>
                      <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
                        {s.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>

                    {s.evidence_description && (
                      <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#4b5563' }}>{s.evidence_description}</p>
                    )}

                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#9ca3af', marginBottom: s.reviewer_comments ? '12px' : '0', flexWrap: 'wrap' }}>
                      <span>📎 {s.file_name}</span>
                      <span>📅 {new Date(s.submission_date).toLocaleDateString()}</span>
                      {s.file_size && <span>{(s.file_size / 1024).toFixed(1)} KB</span>}
                    </div>

                    {s.reviewer_comments && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '12px', marginTop: '12px' }}>
                        <p style={{ margin: 0, fontWeight: '600', fontSize: '13px', color: '#92400e' }}>
                          {rev ? `Comments from ${rev}:` : 'Reviewer Comments:'}
                        </p>
                        <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#374151' }}>{s.reviewer_comments}</p>
                      </div>
                    )}

                    <button onClick={() => handleDownload(s.id, s.file_name)} style={{
                      marginTop: '14px', padding: '7px 16px', background: '#1a3a5c', color: '#fff',
                      border: 'none', borderRadius: '5px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    }}>Download</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Evidence;
